/**
 * Admin API
 *
 * Mock admin service for audit logs, flagged content, role management.
 * Based on Doc 9: Admin & Analytics
 */

import type {
  AuditLogEntry,
  FlaggedContent,
  FlaggedContentStatus,
  AdminRoleAssignment,
  AdminRole,
  AdminUserDetail,
  ModerationAction,
  ModerationActionType,
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  UUID,
  User,
  UserRole,
  AuthTokens,
  TrackRecord,
  TrackRecordEntry,
  TrackRecordEntryMedia,
  XPEvent,
} from '../types'
import { mockDb, generateId, timestamp, delay } from './mockDb'
import { safeExecute, paginated, generateMockToken, success } from './client'

// ---------------------------------------------------------------------------
// Verification Queue Types
// ---------------------------------------------------------------------------

export interface VerificationEntry {
  id: string
  textContent: string
  media: { id: string; type: string; url: string }[]
}

export interface VerificationItem {
  id: string
  userId: string
  userName: string
  challengeId: string
  challengeTitle: string
  submittedAt: string
  verificationAttempt: number
  maxAttempts: number
  entries: VerificationEntry[]
}

const SESSION_KEY = 'docurious-session'

function getCurrentUserId(): string | null {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored).userId
  } catch {
    return null
  }
}

// Seed some mock data
function ensureSeedData() {
  const existing = mockDb.getAll<AuditLogEntry>('auditLogs')
  if (existing.length > 0) return

  const now = new Date()
  const actions = [
    { action: 'user.suspend', resourceType: 'user', details: { reason: 'Repeated policy violations' } },
    { action: 'challenge.approve', resourceType: 'challenge', details: { title: 'Urban Photography Walk' } },
    { action: 'content.remove', resourceType: 'post', details: { reason: 'Inappropriate content' } },
    { action: 'vendor.approve', resourceType: 'vendor', details: { companyName: 'Adventure Labs' } },
    { action: 'school.onboard', resourceType: 'school', details: { schoolName: 'Lincoln Elementary' } },
    { action: 'role.assign', resourceType: 'user', details: { role: 'content', assignee: 'Jane Smith' } },
    { action: 'settings.update', resourceType: 'system', details: { setting: 'maintenance_mode', value: false } },
    { action: 'challenge.feature', resourceType: 'challenge', details: { title: 'Nature Journal' } },
  ]

  for (let i = 0; i < actions.length; i++) {
    const d = new Date(now.getTime() - i * 3600000)
    mockDb.insertWithId<AuditLogEntry>('auditLogs', {
      id: generateId(),
      adminUserId: 'admin-1',
      adminDisplayName: i % 2 === 0 ? 'Admin User' : 'Content Manager',
      action: actions[i].action,
      resourceType: actions[i].resourceType,
      resourceId: generateId(),
      details: actions[i].details,
      ipAddress: '192.168.1.1',
      createdAt: d.toISOString(),
      updatedAt: d.toISOString(),
    })
  }

  // Seed flagged content
  const flags = [
    { contentType: 'post' as const, reason: 'inappropriate' as const, contentPreview: 'This post contains language that violates community guidelines...', reporterDisplayName: 'Sarah K.' },
    { contentType: 'track_record' as const, reason: 'spam' as const, contentPreview: 'Buy cheap products at mysite.com!!!', reporterDisplayName: 'Mike T.' },
    { contentType: 'comment' as const, reason: 'harassment' as const, contentPreview: 'You are terrible at this and should quit...', reporterDisplayName: 'Anna L.' },
    { contentType: 'user_profile' as const, reason: 'privacy' as const, contentPreview: 'Profile contains personal phone number', reporterDisplayName: 'David R.' },
  ]

  for (const flag of flags) {
    mockDb.insert<FlaggedContent>('flaggedContent', {
      reporterId: generateId(),
      reporterDisplayName: flag.reporterDisplayName,
      contentType: flag.contentType,
      contentId: generateId(),
      contentPreview: flag.contentPreview,
      reason: flag.reason,
      status: 'pending',
    } as FlaggedContent)
  }
}

/**
 * Admin API
 */
export const adminApi = {
  async getUsers(): Promise<ApiResponse<Array<{
    id: string; email: string; displayName: string; role: UserRole;
    accountStatus: string; isSuspended: boolean; schoolId?: string; createdAt: string;
  }>>> {
    return safeExecute(() => {
      const allUsers = mockDb.getAll<User & { passwordHash?: string }>('users')
      return allUsers.map((u) => ({
        id: u.id,
        email: u.email,
        displayName: u.displayName,
        role: u.role,
        accountStatus: u.accountStatus ?? 'active',
        isSuspended: u.accountStatus === 'suspended',
        schoolId: u.schoolId,
        createdAt: u.createdAt,
      }))
    })
  },

  async getAuditLogs(params?: PaginationParams & { action?: string; resourceType?: string }): Promise<PaginatedResponse<AuditLogEntry>> {
    ensureSeedData()
    const allLogs = mockDb.getAll<AuditLogEntry>('auditLogs')

    let filtered = allLogs
    if (params?.action) {
      filtered = filtered.filter(l => l.action.includes(params.action!))
    }
    if (params?.resourceType) {
      filtered = filtered.filter(l => l.resourceType === params.resourceType)
    }

    filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    return paginated(filtered, params)
  },

  async getFlaggedContent(status?: FlaggedContentStatus): Promise<ApiResponse<FlaggedContent[]>> {
    ensureSeedData()
    return safeExecute(() => {
      let flags = mockDb.getAll<FlaggedContent>('flaggedContent')
      if (status) {
        flags = flags.filter(f => f.status === status)
      }
      flags.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      return flags
    })
  },

  async resolveFlag(flagId: UUID, action: ModerationActionType, resolution?: string): Promise<ApiResponse<FlaggedContent>> {
    return safeExecute(() => {
      const adminId = getCurrentUserId()
      const flag = mockDb.findById<FlaggedContent>('flaggedContent', flagId)
      if (!flag) throw new Error('Flag not found')

      const updated = mockDb.update<FlaggedContent>('flaggedContent', flagId, {
        status: 'actioned',
        reviewedBy: adminId ?? undefined,
        reviewedAt: timestamp(),
        resolution: resolution ?? action,
      } as Partial<FlaggedContent>)

      // Create moderation action record
      if (adminId) {
        mockDb.insert<ModerationAction>('moderationActions', {
          adminUserId: adminId,
          targetType: 'content',
          targetId: flag.contentId,
          action,
          reason: resolution ?? `Flagged content resolved: ${action}`,
        } as ModerationAction)
      }

      return updated!
    })
  },

  async getUserDetail(userId: UUID): Promise<ApiResponse<AdminUserDetail>> {
    return safeExecute(() => {
      const user = mockDb.findById<Record<string, unknown>>('users', userId)
      if (!user) throw new Error('User not found')

      const modHistory = mockDb.findWhere<ModerationAction>(
        'moderationActions',
        (a) => a.targetId === userId && a.targetType === 'user'
      )

      return {
        id: userId,
        email: (user.email as string) ?? 'user@example.com',
        displayName: (user.displayName as string) ?? 'Unknown User',
        avatarUrl: user.avatarUrl as string | undefined,
        role: (user.role as string) ?? 'general',
        ageTier: user.ageTier as string | undefined,
        accountStatus: (user.accountStatus as 'active' | 'suspended' | 'deleted') ?? 'active',
        schoolId: user.schoolId as string | undefined,
        schoolName: user.schoolName as string | undefined,
        joinedAt: (user.createdAt as string) ?? timestamp(),
        lastActiveAt: user.lastActiveAt as string | undefined,
        challengesCompleted: 12,
        trackRecordsCreated: 8,
        communitiesJoined: 3,
        totalXp: 1250,
        flagCount: modHistory.length,
        moderationHistory: modHistory,
      } as AdminUserDetail
    })
  },

  async getAdminRoles(): Promise<ApiResponse<AdminRoleAssignment[]>> {
    return safeExecute(() => {
      return mockDb.getAll<AdminRoleAssignment>('adminRoles')
    })
  },

  async assignAdminRole(userId: UUID, role: AdminRole): Promise<ApiResponse<AdminRoleAssignment>> {
    return safeExecute(() => {
      const adminId = getCurrentUserId()

      // Remove existing role
      const existing = mockDb.findOne<AdminRoleAssignment>(
        'adminRoles',
        (r) => r.userId === userId
      )
      if (existing) {
        mockDb.delete('adminRoles', existing.id)
      }

      return mockDb.insert<AdminRoleAssignment>('adminRoles', {
        userId,
        role,
        grantedBy: adminId ?? 'system',
        grantedAt: timestamp(),
      } as AdminRoleAssignment)
    })
  },

  async removeAdminRole(userId: UUID): Promise<ApiResponse<boolean>> {
    return safeExecute(() => {
      const existing = mockDb.findOne<AdminRoleAssignment>(
        'adminRoles',
        (r) => r.userId === userId
      )
      if (existing) {
        return mockDb.delete('adminRoles', existing.id)
      }
      return false
    })
  },

  // ============================================================================
  // Verification Queue
  // ============================================================================

  /**
   * Get pending verifications — track records with status 'submitted',
   * joined with user and challenge data.
   */
  async getPendingVerifications(): Promise<ApiResponse<VerificationItem[]>> {
    await delay(150)
    return safeExecute(() => {
      const trackRecords = mockDb.getAll<TrackRecord>('trackRecords')
      const submitted = trackRecords.filter(tr => tr.status === 'submitted')

      const items: VerificationItem[] = submitted.map(tr => {
        const user = mockDb.findById<User>('users', tr.userId)
        const challenge = mockDb.findById<{ id: string; title: string }>('challenges', tr.challengeId)
        const entries = mockDb.findWhere<TrackRecordEntry>(
          'trackRecordEntries',
          (e) => e.trackRecordId === tr.id
        )

        return {
          id: tr.id,
          userId: tr.userId,
          userName: user?.displayName ?? 'Unknown User',
          challengeId: tr.challengeId,
          challengeTitle: challenge?.title ?? 'Unknown Challenge',
          submittedAt: tr.submittedAt ?? tr.updatedAt,
          verificationAttempt: tr.verificationAttempts ?? 1,
          maxAttempts: 3,
          entries: entries.map(e => {
            const media = mockDb.findWhere<TrackRecordEntryMedia>(
              'trackRecordMedia',
              (m) => m.entryId === e.id
            )
            return {
              id: e.id,
              textContent: e.textContent ?? '',
              media: media.map(m => ({
                id: m.id,
                type: m.mediaType ?? 'image',
                url: m.url,
              })),
            }
          }),
        }
      })

      return items
    })
  },

  /**
   * Approve a track record verification — sets status to 'verified' and creates an XP event.
   */
  async approveVerification(trackRecordId: UUID): Promise<ApiResponse<boolean>> {
    await delay(200)
    return safeExecute(() => {
      const tr = mockDb.findById<TrackRecord>('trackRecords', trackRecordId)
      if (!tr) throw new Error('Track record not found')

      mockDb.update<TrackRecord>('trackRecords', trackRecordId, {
        status: 'verified',
        verifiedAt: timestamp(),
      } as Partial<TrackRecord>)

      // Create XP event for challenge verification
      mockDb.insert<XPEvent>('xpEvents', {
        userId: tr.userId,
        eventType: 'challenge_completed',
        amount: 100,
        metadata: { trackRecordId, challengeId: tr.challengeId },
        createdAt: timestamp(),
        updatedAt: timestamp(),
      } as unknown as XPEvent)

      return true
    })
  },

  /**
   * Reject a track record verification — sets status to 'rejected'.
   */
  async rejectVerification(trackRecordId: UUID, reason: string, note?: string): Promise<ApiResponse<boolean>> {
    await delay(200)
    return safeExecute(() => {
      const tr = mockDb.findById<TrackRecord>('trackRecords', trackRecordId)
      if (!tr) throw new Error('Track record not found')

      mockDb.update<TrackRecord>('trackRecords', trackRecordId, {
        status: 'rejected',
      } as Partial<TrackRecord>)

      // Create audit log entry for the rejection
      const adminId = getCurrentUserId()
      if (adminId) {
        mockDb.insert<AuditLogEntry>('auditLogs', {
          adminUserId: adminId,
          adminDisplayName: 'Admin',
          action: 'verification.reject',
          resourceType: 'track_record',
          resourceId: trackRecordId,
          details: { reason, note },
          ipAddress: '127.0.0.1',
          createdAt: timestamp(),
          updatedAt: timestamp(),
        } as unknown as AuditLogEntry)
      }

      return true
    })
  },

  /**
   * Impersonate a user — admin-authorized session switch without password.
   * Returns the target user and fresh tokens.
   */
  async impersonateUser(userId: UUID): Promise<ApiResponse<{ user: User; tokens: AuthTokens }>> {
    return safeExecute(() => {
      const userRecord = mockDb.findById<User & { passwordHash?: string }>('users', userId)
      if (!userRecord) throw new Error('User not found')

      // Generate tokens for the target user
      const accessToken = generateMockToken(userId, 24)
      const refreshToken = generateMockToken(userId, 168)
      const tokens: AuthTokens = {
        accessToken,
        refreshToken,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      }

      // Save session as the impersonated user
      localStorage.setItem(SESSION_KEY, JSON.stringify({ userId, tokens }))

      // Strip password fields
      const { passwordHash, ...user } = userRecord as User & { passwordHash?: string }
      return { user: user as User, tokens }
    })
  },
}
